import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock, faTrophy, faRocket, faShieldAlt, faArrowRight, faArrowLeft, faKey, faBrain } from '@fortawesome/free-solid-svg-icons';
import PlatformVideo from './PlatformVideo';
import {
  PlatformContainer,
  PlatformHeader,
  PlatformIcon,
  PlatformTitle,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  ProgressText,
  StepsList,
  StepItem,
  StepNumber,
  StepContent,
  StepText,
  StepLink,
  CompletedCheckmark,
  StepToggle,
  StepDescription,
  TechCircle,
  SuccessMessage,
  AnimatedBorder,
  TechGrid,
  DataStream,
  NavigationButtons,
  NavigationButton,
  FinalSuccessMessage
} from './PlatformStyles';

const OpenAIPlatform = ({ completedSteps, onStepComplete, allPlatformsComplete, onUpdateCredential, onSubmitCredentials, platformColor, activeStep, onUpdateActiveStep }) => {
  const [progress, setProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [credentials, setCredentials] = useState({
    openaiApiKey: ''
  });

  const platformUrl = 'https://platform.openai.com/signup';

  const steps = [
    {
      title: 'Criar conta no OpenAI e fornecer API Key',
      description: 'Acesse o site oficial da OpenAI, crie sua conta, adicione seu cartão e $5 dolares em créditos, crie uma API Key e envie aqui.',
      icon: faBrain,
      isCredential: true,
      inputLabel: 'API Key',
      inputName: 'openaiApiKey'
    }
  ];

  useEffect(() => {
    const completed = completedSteps.filter(step => step).length;
    const total = completedSteps.length;
    setProgress(Math.round((completed / total) * 100));

    if (completed === total && total > 0) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [completedSteps]);

  const handleCompleteStep = () => {
    // Marcar a tarefa atual como concluída
    onStepComplete(activeStep);
    
    // Definir que está em transição
    setIsTransitioning(true);

    // Adicionar um delay maior para permitir que o usuário veja a animação
    setTimeout(() => {
      // Avançar automaticamente para o próximo passo após marcar como concluído
      if (activeStep < steps.length - 1) {
        onUpdateActiveStep(activeStep + 1);
      }
      
      // Finalizar a transição
      setIsTransitioning(false);
    }, 800);
  };

  const handleNextStep = () => {
    // Evitar múltiplos cliques durante a transição ou submissão
    if (isTransitioning || isSubmitting) return;
    
    // Verificar se os campos de credenciais estão preenchidos no último passo
    if (activeStep === steps.length - 1) {
      const { openaiApiKey } = credentials;
      if (!openaiApiKey) {
        // Alertar o usuário que precisa preencher todos os campos
        console.warn('Por favor, preencha a API Key da OpenAI antes de prosseguir.');
        return;
      }
    }
    
    // Definir que está em transição
    setIsTransitioning(true);
    
    // Marcar a tarefa atual como concluída
    if (!completedSteps[activeStep]) {
      onStepComplete(activeStep);
    }

    // Adicionar um delay maior para permitir que o usuário veja a animação
    setTimeout(() => {
      // Se for o último passo, apenas mostrar a mensagem de parabéns
      if (activeStep === steps.length - 1) {
        // Não redirecionar para a próxima plataforma
        // Apenas completar o último passo
        if (onSubmitCredentials) {
          setShowError(false); // Limpar erro anterior
          setIsSubmitting(true); // Indicar que está enviando
          onSubmitCredentials()
            .then(() => {
              setIsSubmitting(false);
              setShowSuccessMessage(true); // Mostrar mensagem de sucesso apenas após o webhook retornar
              setIsTransitioning(false); // Finalizar a transição
            })
            .catch(error => {
              console.error('Erro ao enviar credenciais:', error);
              setShowError(true);
              setIsSubmitting(false);
              setIsTransitioning(false); // Finalizar a transição mesmo em caso de erro
            });
        } else {
          setIsTransitioning(false); // Finalizar a transição se não houver submissão
        }
      } else {
        // Caso contrário, avançar para o próximo passo
        onUpdateActiveStep(activeStep + 1);
        setIsTransitioning(false); // Finalizar a transição
      }
    }, 800);
  };

  const handlePrevStep = () => {
    // Evitar múltiplos cliques durante a transição ou submissão
    if (isTransitioning || isSubmitting) return;
    
    // Definir que está em transição
    setIsTransitioning(true);
    
    if (activeStep > 0) {
      // Adicionar uma animação suave para voltar
      setTimeout(() => {
        onUpdateActiveStep(activeStep - 1);
        
        // Finalizar a transição
        setIsTransitioning(false);
      }, 150);
    } else {
      // Finalizar a transição se não houver navegação
      setIsTransitioning(false);
    }
  };

  const isAllComplete = progress === 100;
  const currentStep = steps[activeStep];

  return (
    <PlatformContainer>
      <TechGrid />
      <DataStream />



      <PlatformHeader>
        <PlatformIcon color={platformColor}>
          <FontAwesomeIcon icon={faBrain} />
        </PlatformIcon>
        <PlatformTitle color={platformColor}>OpenAI</PlatformTitle>
      </PlatformHeader>

      <PlatformVideo 
        platformId="openai" 
        platformColor={platformColor} 
      />

      <StepsList>
        <StepItem
          key={activeStep}
          completed={completedSteps[activeStep]}
          active={true}
        >
          <AnimatedBorder active={true} color={platformColor} />


          <StepContent>
            <StepText>{currentStep.title}</StepText>
            <StepDescription>{currentStep.description}</StepDescription>
            {activeStep === 0 && (
              <StepLink
                href={platformUrl}
                target="_blank"
                rel="noopener noreferrer"
                color={platformColor}
              >
                Acessar OpenAI
              </StepLink>
            )}
            {currentStep.isCredential && (
              <div style={{ marginTop: '15px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}>
                  {currentStep.inputLabel}
                </label>
                <input
                  type="text"
                  name={currentStep.inputName}
                  value={credentials[currentStep.inputName] || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCredentials(prev => ({
                      ...prev,
                      [currentStep.inputName]: value
                    }));
                    onUpdateCredential && onUpdateCredential('openai', currentStep.inputName, value);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: `1px solid ${platformColor}`,
                    borderRadius: '4px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}
          </StepContent>


        </StepItem>
      </StepsList>

      <NavigationButtons>
        <NavigationButton 
          onClick={handlePrevStep} 
          disabled={activeStep === 0 || isTransitioning || isSubmitting}
          style={{ 
            opacity: (activeStep === 0 || isTransitioning || isSubmitting) ? 0.5 : 1,
            cursor: (activeStep === 0 || isTransitioning || isSubmitting) ? 'not-allowed' : 'pointer',
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Anterior
        </NavigationButton>

        <NavigationButton
          onClick={handleNextStep}
          disabled={isSubmitting || isTransitioning}
          style={{
            opacity: (isSubmitting || isTransitioning) ? 0.7 : 1,
            cursor: (isSubmitting || isTransitioning) ? 'not-allowed' : 'pointer',
            background: `linear-gradient(to right, ${platformColor}, ${platformColor}CC)`,
            color: 'white',
            border: 'none',
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {isSubmitting ? 'Enviando...' : (activeStep === steps.length - 1 ? 'Concluir' : 'Próximo')} 
          {!isSubmitting && <FontAwesomeIcon icon={faArrowRight} />}
        </NavigationButton>
      </NavigationButtons>

      {showError && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: 'rgba(220, 53, 69, 0.2)',
          border: '1px solid #dc3545',
          borderRadius: '5px',
          color: '#fff',
          textAlign: 'center'
        }}>
          Ocorreu um erro ao enviar suas credenciais. Por favor, verifique se todas as informações estão corretas e tente novamente.
        </div>
      )}

      {allPlatformsComplete && showSuccessMessage && (
        <FinalSuccessMessage>
          <FontAwesomeIcon icon={faTrophy} size="2x" />
          <h3>Parabéns! Todas as plataformas foram configuradas com sucesso!</h3>
          <p>
            Você completou todas as configurações necessárias para utilizar a BIANCA.
          </p>
          <NavigationButton
            onClick={() => window.location.href = 'https://app.abianca.com.br'}
            style={{
              margin: '2rem auto 0.5rem',
              background: 'linear-gradient(135deg, #4C6EF5, #00A67E)',
              color: 'white',
              border: 'none',
              padding: '0.9rem 2rem',
              borderRadius: '30px',
              fontSize: '1rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 15px rgba(76, 110, 245, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.7rem',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(76, 110, 245, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(76, 110, 245, 0.4)';
            }}
          >
            Acessar BIANCA <FontAwesomeIcon icon={faRocket} />
          </NavigationButton>
        </FinalSuccessMessage>
      )}
    </PlatformContainer>
  );
};

export default OpenAIPlatform;
